<template>
  <div class="navbar-container" :class="{ 'transparent': isTransparent, 'scrolled': isScrolled }">
    <div class="navbar-left">
      <div v-if="logoUrl" class="logo-wrapper" @click="$router.push('/')">
        <img :src="logoUrl" :alt="siteName" class="logo" />
      </div>
    </div>
    
    <a-menu 
      mode="horizontal" 
      :selected-keys="selectedKeys"
      class="navbar-menu"
    >
      <a-menu-item key="home" @click="$router.push('/')">首页</a-menu-item>
      <a-menu-item key="category" @click="$router.push('/category')">商品分类</a-menu-item>
      <a-menu-item 
        v-for="cat in articleCategories" 
        :key="`article-${cat.id}`"
        @click="$router.push(`/articles/${cat.id}`)"
      >
        {{ cat.name }}
      </a-menu-item>
      <a-menu-item key="publish" @click="$router.push('/publish')">发布信息</a-menu-item>
      <a-menu-item key="about" @click="$router.push('/about')">关于我们</a-menu-item>
    </a-menu>
    
    <div class="navbar-actions">
      <!-- 购物车图标 -->
      <div class="cart-icon" @click="$router.push('/cart')">
        <a-badge :count="cartCount" :offset="[0, 5]">
          <svg class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
            <path d="M831.398957 788.813913c-47.037217 0-85.170087 38.13287-85.170087 85.170087 0 47.037217 38.13287 85.170087 85.170087 85.170087s85.170087-38.13287 85.170087-85.170087C916.569043 826.946783 878.436174 788.813913 831.398957 788.813913zM831.398957 916.569043c-23.529739 0-42.585043-19.055304-42.585043-42.585043s19.055304-42.585043 42.585043-42.585043 42.585043 19.055304 42.585043 42.585043S854.906435 916.569043 831.398957 916.569043zM384.24487 788.813913c-47.037217 0-85.170087 38.13287-85.170087 85.170087 0 47.037217 38.13287 85.170087 85.170087 85.170087 47.037217 0 85.170087-38.13287 85.170087-85.170087C469.414957 826.946783 431.282087 788.813913 384.24487 788.813913zM384.24487 916.569043c-23.529739 0-42.585043-19.055304-42.585043-42.585043s19.055304-42.585043 42.585043-42.585043 42.585043 19.055304 42.585043 42.585043S407.752348 916.569043 384.24487 916.569043zM953.165913 557.278609c0 0 48.573217-278.238609 48.573217-279.507478 0-47.037217-38.13287-85.170087-85.170087-85.170087L201.371826 192.601043 188.638609 125.68487C178.109217 90.579478 145.942261 64.845913 107.430957 64.845913l-63.888696 0C31.788522 64.845913 22.26087 74.373565 22.26087 86.127304c0 11.776 9.527652 21.303652 21.303652 21.303652l63.888696 0c20.413217 0 37.420522 14.380522 41.583304 33.52487l93.718261 531.545043c0.400696 3.027478 2.470957 11.820522 2.470957 11.820522 9.616696 33.636174 39.134609 59.169391 75.152696 61.885217l596.190609 0c11.753739 0 21.303652-9.527652 21.303652-21.281391 0-11.776-9.549913-21.303652-21.303652-21.303652L326.745043 703.621565c-19.366957 0-35.528348-13.022609-40.71513-30.72l-9.594435-54.450087 595.411478 0C910.491826 618.451478 942.770087 592.562087 953.165913 557.278609zM268.933565 575.866435 208.873739 235.186087l707.695304 0c23.529739 0 42.585043 19.055304 42.585043 42.585043 0 7.791304-46.83687 268.087652-46.83687 268.087652-5.409391 17.341217-21.348174 29.985391-40.470261 29.985391L268.933565 575.844174z" fill="currentColor"></path>
          </svg>
        </a-badge>
      </div>
      
      <!-- 未登录状态 -->
      <template v-if="!isLoggedIn">
        <a-button class="login-btn" @click="$router.push('/login')">登录</a-button>
        <a-button type="primary" class="register-btn" @click="$router.push('/register')">注册</a-button>
      </template>
      
      <!-- 已登录状态 -->
      <template v-else>
        <div class="user-profile" @click="$router.push('/profile')">
          <img :src="userAvatar" alt="用户头像" class="user-avatar-small" />
          <span class="user-nickname">{{ userNickname }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { getSiteConfig, getArticleCategories } from '@/api/index.js'

export default {
  name: 'NavBar',
  data() {
    return {
      logoUrl: '',
      siteName: '',
      isLoggedIn: false,
      userNickname: '用户',
      userAvatar: '',
      cartCount: 0,
      articleCategories: [],
      isTransparent: false,
      isScrolled: false
    }
  },
  computed: {
    selectedKeys() {
      const path = this.$route.path
      if (path === '/') return ['home']
      if (path === '/category') return ['category']
      if (path.startsWith('/articles/')) {
        const id = path.split('/')[2]
        return [`article-${id}`]
      }
      if (path === '/publish') return ['publish']
      if (path === '/about') return ['about']
      return []
    }
  },
  created() {
    // 在created时就设置初始状态，比mounted更早
    if (this.$route.path === '/') {
      this.isTransparent = true
      this.isScrolled = false
    } else {
      this.isTransparent = false
      this.isScrolled = false
    }
  },
  mounted() {
    this.loadSiteConfig()
    this.loadArticleCategories()
    this.checkLoginStatus()
    this.updateCartCount()
    
    // 添加滚动监听（仅首页生效）
    window.addEventListener('scroll', this.handleScroll)
    
    // 监听购物车更新事件
    window.addEventListener('cart-updated', this.updateCartCount)
  },
  beforeUnmount() {
    window.removeEventListener('cart-updated', this.updateCartCount)
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    async loadSiteConfig() {
      try {
        const result = await getSiteConfig()
        if (result.code === 1 && result.data) {
          if (result.data.site_logo_url) {
            this.logoUrl = result.data.site_logo_url
            // 同时设置favicon
            this.setFavicon(result.data.site_logo_url)
          }
          if (result.data.site_name) {
            this.siteName = result.data.site_name
            // 设置网页标题
            document.title = result.data.site_name
          }
          // 设置meta信息
          if (result.data.site_description) {
            this.setMetaTag('description', result.data.site_description)
          }
          if (result.data.site_keywords) {
            this.setMetaTag('keywords', result.data.site_keywords)
          }
        }
      } catch (error) {
        console.error('加载网站配置失败:', error)
      }
    },
    async loadArticleCategories() {
      try {
        const result = await getArticleCategories()
        if (result.code === 1 && result.data) {
          this.articleCategories = result.data
        }
      } catch (error) {
        console.error('加载文章分类失败:', error)
      }
    },
    setMetaTag(name, content) {
      let meta = document.querySelector(`meta[name="${name}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.name = name
        document.head.appendChild(meta)
      }
      meta.content = content
    },
    setFavicon(url) {
      let link = document.querySelector("link[rel~='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = url
    },
    checkLoginStatus() {
      const token = localStorage.getItem('token')
      const userInfo = localStorage.getItem('userInfo')
      
      if (token && userInfo) {
        this.isLoggedIn = true
        try {
          const user = JSON.parse(userInfo)
          this.userNickname = user.nickname || user.username || '用户'
          this.userAvatar = user.avatar || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiMxODkwZmYiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1zaXplPSIxOCIgZmlsbD0iI2ZmZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPnU8L3RleHQ+PC9zdmc+'
        } catch (error) {
          console.error('解析用户信息失败:', error)
          this.isLoggedIn = false
        }
      } else {
        this.isLoggedIn = false
      }
    },
    async updateCartCount() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.cartCount = 0
        return
      }
      
      try {
        // 动态导入getCartList API
        const { getCartList } = await import('@/api/index.js')
        const result = await getCartList(token)
        
        if (result.code === 1 && result.data) {
          this.cartCount = result.data.length
        } else {
          this.cartCount = 0
        }
      } catch (error) {
        console.error('获取购物车数量失败:', error)
        this.cartCount = 0
      }
    },
    handleScroll() {
      // 只在首页才处理滚动事件
      if (this.$route.path !== '/') {
        return
      }
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      this.isScrolled = scrollTop > 50
      this.isTransparent = scrollTop < 50
    }
  },
  watch: {
    '$route'(to) {
      // 路由切换时立即设置状态
      if (to.path === '/') {
        this.isTransparent = true
        this.isScrolled = false
      } else {
        this.isTransparent = false
        this.isScrolled = false
      }
    }
  }
}
</script>

<style scoped>
.navbar-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  transition: all 0.3s ease;
}

.navbar-left {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 660px;
  flex-shrink: 0;
}

.logo-wrapper {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.navbar-menu {
  flex: 1;
  display: flex;
  justify-content: center;
}

.navbar-menu :deep(.arco-menu-horizontal) {
  flex: 1;
  max-width: 1000px;
  overflow: visible;
}

.navbar-menu :deep(.arco-menu-inner) {
  flex-wrap: nowrap;
  overflow: visible;
}

.navbar-menu :deep(.arco-menu-item) {
  white-space: nowrap;
  font-size: 18px;
  flex-shrink: 0;
}

.navbar-container.transparent {
  background-color: transparent;
  box-shadow: none;
}

.navbar-container.transparent :deep(.arco-menu-horizontal) {
  background-color: transparent !important;
}

.navbar-container.transparent :deep(.arco-menu-item) {
  color: #ffffff !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  background-color: transparent !important;
}

.navbar-container.transparent :deep(.arco-menu-item:hover) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.navbar-container.transparent .cart-icon,
.navbar-container.transparent .login-btn,
.navbar-container.transparent .user-nickname {
  color: #ffffff !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.navbar-container.transparent .login-btn {
  border-color: rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.navbar-container.transparent .login-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: #fff;
}

.navbar-container.transparent .register-btn {
  background: rgba(24, 144, 255, 0.85) !important;
  border-color: transparent !important;
}

.navbar-container.transparent .register-btn:hover {
  background: rgba(64, 169, 255, 0.95) !important;
}

.navbar-container.transparent :deep(.arco-menu-item.arco-menu-selected) {
  color: #ffffff !important;
  font-weight: 600;
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.navbar-container.transparent .logo {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.navbar-container.transparent .logo:hover {
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.4)) brightness(1.1);
  transform: scale(1.05);
}

.navbar-container.transparent .cart-icon:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.navbar-container.transparent :deep(.arco-badge-number) {
  background: #1890ff;
  border: 2px solid rgba(255, 255, 255, 0.9);
}

.navbar-container :deep(.arco-menu-horizontal) {
  border-bottom: none;
  padding: 12px 20px;
  font-size: 16px;
}

.navbar-container :deep(.arco-menu-item.arco-menu-selected) {
  color: #1890ff !important;
}

.navbar-container :deep(.arco-menu-item::before),
.navbar-container :deep(.arco-menu-item::after) {
  display: none !important;
  content: none !important;
  background: none !important;
  height: 0 !important;
  opacity: 0 !important;
}

.navbar-container :deep(.arco-menu-selected-label) {
  display: none !important;
}

.logo {
  height: 65px;
  width: auto;
  display: block;
  cursor: pointer;
  transition: all 0.3s ease;
}

.logo:hover {
  transform: scale(1.05);
  filter: brightness(1.1);
}

.navbar-actions {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  width: 150px;
  flex-shrink: 0;
}

.cart-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
}

.cart-icon:hover {
  background: rgba(24, 144, 255, 0.1);
  color: #1890ff;
}

.cart-icon :deep(.arco-badge-number) {
  background: #1890ff;
  border: none;
}

.login-btn {
  padding: 8px 24px;
  height: 36px;
  border-radius: 18px;
  font-size: 14px;
  border: 1px solid #e5e6eb;
  background: #fff;
  color: #666;
  transition: all 0.3s ease;
}

.login-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
  background: rgba(24, 144, 255, 0.05);
}

.register-btn {
  padding: 8px 24px;
  height: 36px;
  border-radius: 18px;
  font-size: 14px;
  background: #1890ff !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.register-btn:hover {
  background: #40a9ff !important;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(24, 144, 255, 0.05);
}

.user-profile:hover {
  background: rgba(24, 144, 255, 0.1);
}

.user-avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #1890ff;
}

.user-nickname {
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
